// Habit icons are lucide icon names (kebab-case); web and native map them to
// their own lucide package. `icon` on a habit node is optional — unset habits
// show guessHabitIcon(title, category).

export const HABIT_ICON_GROUPS: { key: string; icons: string[] }[] = [
  {
    key: 'fitness',
    icons: [
      'dumbbell',
      'footprints',
      'bike',
      'waves',
      'person-standing',
      'heart-pulse',
      'mountain',
      'timer',
      'trophy',
    ],
  },
  {
    key: 'mind',
    icons: [
      'brain',
      'book-open',
      'leaf',
      'wind',
      'sparkles',
      'sun',
      'moon',
      'pen-line',
      'lightbulb',
      'graduation-cap',
      'languages',
    ],
  },
  {
    key: 'health',
    icons: [
      'droplet',
      'apple',
      'salad',
      'carrot',
      'pill',
      'bed-double',
      'smile',
      'coffee',
      'shower-head',
      'snowflake',
      'phone-off',
      'cigarette-off',
      'wine-off',
    ],
  },
  {
    key: 'work',
    icons: [
      'briefcase',
      'laptop',
      'code',
      'list-checks',
      'target',
      'clock',
      'hourglass',
      'calendar',
      'mail',
    ],
  },
  { key: 'money', icons: ['wallet', 'piggy-bank', 'coins', 'receipt', 'trending-up'] },
  {
    key: 'home',
    icons: ['house', 'utensils', 'shirt', 'sprout', 'flower-2', 'trash-2', 'dog', 'cat', 'baby'],
  },
  {
    key: 'social',
    icons: ['users', 'phone', 'message-circle', 'heart', 'hand-heart', 'gift', 'ear'],
  },
  {
    key: 'creative',
    icons: [
      'palette',
      'pencil',
      'music',
      'guitar',
      'camera',
      'mic',
      'hammer',
      'gamepad-2',
      'plane',
      'star',
      'flame',
      'zap',
    ],
  },
]

export const HABIT_ICONS: string[] = HABIT_ICON_GROUPS.flatMap((g) => g.icons)

const CATEGORY_ICON: Record<string, string> = {
  health: 'heart-pulse',
  mind: 'brain',
  career: 'briefcase',
  finance: 'wallet',
  relationships: 'heart',
  creative: 'palette',
}

// First match wins, so specific phrases ("water the plants", "walk the dog")
// sit above the generic words they contain.
const KEYWORDS: [RegExp, string][] = [
  [/plant|garden/, 'sprout'],
  [/\bdog\b|puppy/, 'dog'],
  [/\bcat\b|kitten/, 'cat'],
  [/water|hydrat|drink.*(glass|litre|liter)/, 'droplet'],
  [/\brun|jog|marathon|\b\d+\s?k\b/, 'footprints'],
  [/walk|steps|stairs|hike/, 'footprints'],
  [/gym|lift|workout|strength|push-?up|squat|plank|exercise/, 'dumbbell'],
  [/bike|cycl/, 'bike'],
  [/swim/, 'waves'],
  [/yoga|stretch|mobility|posture/, 'person-standing'],
  [/meditat|mindful|stillness/, 'leaf'],
  [/cold (shower|plunge|exposure|water)|ice bath/, 'snowflake'],
  [/breath/, 'wind'],
  [/read|book|pages/, 'book-open'],
  [/journal|gratitude|diary/, 'pen-line'],
  [/write|writing|blog/, 'pencil'],
  [/sleep|bed|nap/, 'bed-double'],
  [/sun|wake|morning/, 'sun'],
  [/vitamin|medic|pill|supplement/, 'pill'],
  [/veg|salad|fruit|healthy (meal|food|eat)/, 'salad'],
  [/cook|meal|breakfast|lunch|dinner/, 'utensils'],
  [/coffee|caffeine|tea\b/, 'coffee'],
  [/alcohol|beer|wine|sober/, 'wine-off'],
  [/smok|vape|cigar/, 'cigarette-off'],
  [/phone|screen|social media|scroll|device/, 'phone-off'],
  [/floss|teeth|skin|shower|groom/, 'shower-head'],
  [/learn|study|course|class|lesson/, 'graduation-cap'],
  [/spanish|french|japanese|language|duolingo|vocab/, 'languages'],
  [/code|program|leetcode/, 'code'],
  [/email|inbox/, 'mail'],
  [/focus|deep work|priorit/, 'target'],
  [/plan|review|to-?do|list/, 'list-checks'],
  [/save|saving|budget/, 'piggy-bank'],
  [/invest|stock/, 'trending-up'],
  [/spend|expense|receipt/, 'receipt'],
  [/clean|tidy|declutter|dishes|laundry/, 'house'],
  [/call|phone (mum|mom|dad)|parents|family/, 'phone'],
  [/friend|network|connect|social/, 'users'],
  [/partner|date night|love|wife|husband/, 'heart'],
  [/kind|volunteer|donat|help/, 'hand-heart'],
  [/guitar/, 'guitar'],
  [/music|piano|sing|instrument|practi[cs]e/, 'music'],
  [/draw|paint|sketch|art/, 'palette'],
  [/photo/, 'camera'],
  [/podcast|record/, 'mic'],
  [/idea|reflect/, 'lightbulb'],
  [/game|chess/, 'gamepad-2'],
  [/pray|church|spiritual/, 'sparkles'],
]

export function guessHabitIcon(title: string, category?: string | null): string {
  const t = title.toLowerCase()
  for (const [re, icon] of KEYWORDS) if (re.test(t)) return icon
  return CATEGORY_ICON[category?.toLowerCase() ?? ''] ?? 'target'
}

export function habitIcon(h: { icon?: string | null; title: string; category?: string | null }) {
  return h.icon && HABIT_ICONS.includes(h.icon) ? h.icon : guessHabitIcon(h.title, h.category)
}
